import { CommonModule, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { rowRemovingConfirmation } from '@asamco/application-link/shared';
import { AsamcoAuthService, AsamcoUserService } from '@asamco/base';
import { fixOdataDateOffsetFilter, pathJoin, showErrorSwal } from '@asamco/utils';
import { Group, GroupService, UsergroupService, UserInGroupDto } from '@asamco/user-management/shared';
import { Subject } from '@microsoft/signalr';
import { DxButtonModule, DxDataGridModule, DxPopupModule, DxTagBoxModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { LoadOptions } from 'devextreme/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { EditorPreparedEvent, RowRemovingEvent } from 'devextreme/ui/data_grid';
import { Observable, catchError, firstValueFrom, map, of, throwError, timeout } from 'rxjs';
import { AlUserV2Service, UserDto } from '../../../services/al-user-v2.service';
import notify from 'devextreme/ui/notify';
import { FormsModule } from '@angular/forms';

type ViewModel = {
	state: 'loading'
} | {
	state: 'ready'
	dataSource: DataSource;
} | {
	state: 'error'
}

@Component({
	selector: 'app-shell-admin-users-management-grid',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		DxButtonModule,
		DxDataGridModule,
		DxPopupModule,
		DxTagBoxModule,
		DxTemplateModule,
		NgIf,
	],
	templateUrl: './admin-users-management-grid.component.html',
	styleUrl: './admin-users-management-grid.component.scss',
})
export class AdminUsersManagementGridComponent implements OnDestroy {
	private static readonly superApplicationLinkAdministratorsGroupName = 'Super ApplicationLink Administrators';
	private static readonly applicationLinkAdministratorsGroupName = 'ApplicationLink Admins';

	private static readonly hiddenLegacyGroupNames = new Set([
		'Super Administrators',
		'Administrators',
		'Users',
	]);

	private static readonly restrictedGroupNames = new Set([
		'Super Administrators',
		'Administrators',
		'Users',
		AdminUsersManagementGridComponent.superApplicationLinkAdministratorsGroupName,
		AdminUsersManagementGridComponent.applicationLinkAdministratorsGroupName,
	]);

	private readonly destroy$ = new Subject<void>();
	public groupManagementVisible = false;
	public groupManagementLoading = false;
	public groupManagementSaving = false;
	public selectedUser: UserDto | null = null;
	public availableGroups: Group[] = [];
	public selectedGroupIds: number[] = [];
	public reservedAssignedGroups: Group[] = [];
	public canManageProtectedGroups = false;
	public canManageGroups = false;
	public canManageUsers = false;
	public reservedAssignedGroupNames = '';

	public readonly viewModel$: Observable<ViewModel> = of({
		state: 'loading'
	}).pipe(
		map(() => {
			const viewModel: ViewModel = {
				state: 'ready',
				dataSource: this.buildDataSource(),
			};
			return viewModel;
		}),
		catchError(error => {
			showErrorSwal(error);
			const viewModel: ViewModel = {
				state: 'error'
			};
			return of(viewModel);
		}),
	);

	constructor(
		private activatedRoute: ActivatedRoute,
		private asamcoAuthService: AsamcoAuthService,
		private asamcoUserService: AsamcoUserService,
		private changeDetectorRef: ChangeDetectorRef,
		private userService: AlUserV2Service,
		private groupService: GroupService,
		private usergroupService: UsergroupService,
		private router: Router,
	) {
		void this.initializePermissions();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private async initializePermissions(): Promise<void> {
		const [canManageUsers, availableGroups, usersPerGroup] = await Promise.all([
			this.asamcoUserService.hasAssignedPermission('AL3_USER_MANAGE'),
			firstValueFrom(this.groupService.getAll().pipe(timeout(15000))),
			firstValueFrom(this.groupService.getUsersPerGroup('', 0).pipe(timeout(15000))),
		]);
		const groupAccess = this.resolveCurrentUserGroupAccess(availableGroups, usersPerGroup);
		this.canManageProtectedGroups = groupAccess.canManageProtectedGroups;
		this.canManageGroups = groupAccess.canManageGroups;
		this.canManageUsers = canManageUsers;
	}

	private buildDataSource(): DataSource {

		const odataStore = new ODataStore({
			version: 4,
			key: 'id',
			keyType: 'Int64',
			url: pathJoin([this.asamcoAuthService.userSession.backend.odataBaseUrl, 'dynamic/_AL3_vw_Users']),
			beforeSend: this.asamcoAuthService.dxStoreBeforeSend,
			deserializeDates: false,
			onLoading: options => {
				fixOdataDateOffsetFilter(options.filter);
			},
		});
		// odataStore.insert = (values: any) => {
		// 	return fromPromise(
		// 		this.themeItemService
		// 			.createDraft({
		// 				themeId: this.themeId,
		// 				...values,
		// 			})
		// 			.pipe(
		// 				catchError(error => {
		// 					showErrorSwal(error);
		// 					return throwError('Er is een fout opgetreden');
		// 				})
		// 			)
		// 			.toPromise()
		// 	);
		// };
		// odataStore.insert = (values: unknown) => {
		// 	return Deferred();
		// }


		const customStore = new CustomStore<UserDto, number>({
			key: 'id',
			loadMode: 'processed',
			totalCount: (options: LoadOptions) => odataStore.totalCount(options),
			load: (options: LoadOptions) => {
				return odataStore.load(options);
			},
			byKey: (key) => odataStore.byKey(key),
			update: async (key: number, values: UserDto) => {
				return await firstValueFrom(this.userService.update({
					id: key,
					...values,
				}));
			},
			remove: async (key: number) => {
				await firstValueFrom(this.userService.delete(key).pipe(this.handleDataStoreError()));
			},
		});

		const dataSource = new DataSource<UserDto, number>({
			key: 'id',
			requireTotalCount: true,
			paginate: true,
			store: customStore,
		});
		return dataSource;
	}

	private handleDataStoreError<T>(): (source: Observable<T>) => Observable<T> {
		return (source: Observable<T>): Observable<T> =>
			source.pipe(
				catchError(error => {
					showErrorSwal(error)
					// Let Dx do its thing as well.
					return throwError(() => error);
				})
			);
	}

	public rowRemoving($event: RowRemovingEvent<UserDto>, viewModel: ViewModel): void {
		const user = $event.data;

		$event.cancel = rowRemovingConfirmation({
			text: `Delete user '${this.getUserDisplayName(user)}'?`,
		});
	}

	public readonly manageGroupsFromCell = (cell: { data?: UserDto }) => {
		if (!this.canManageGroups) {
			return;
		}

		const user = cell.data;
		if (!user) {
			return;
		}

		void this.openGroupManagement(user);
	};

	public readonly viewPermissionsFromCell = (cell: { data?: UserDto }) => {
		const user = cell.data;
		if (!user?.id) {
			return;
		}

		void this.router.navigate(['./', user.id, 'permissions'], { relativeTo: this.activatedRoute });
	};

	public async saveGroupAssignments(): Promise<void> {
		if (!this.canManageGroups || !this.selectedUser?.id) {
			return;
		}

		this.groupManagementSaving = true;

		try {
			await firstValueFrom(this.usergroupService.updateGroupsForUser(this.selectedUser.id, ...this.selectedGroupIds));
			notify(`Updated groups for ${this.getUserDisplayName(this.selectedUser)}.`, 'success', 2500);
			this.closeGroupManagement();
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.groupManagementSaving = false;
		}
	}

	public closeGroupManagement(): void {
		this.groupManagementVisible = false;
		this.groupManagementLoading = false;
		this.groupManagementSaving = false;
		this.selectedUser = null;
		this.availableGroups = [];
		this.reservedAssignedGroups = [];
		this.reservedAssignedGroupNames = '';
		this.selectedGroupIds = [];
	}

	public onEditorPreparing(event: EditorPreparedEvent<UserDto>) {
		if (event.parentType !== "dataRow") {
			return;
		}

		if (event.row.isNewRow) {
			return;
		}

		if (['userName', 'firstName', 'lastName', 'email'].findIndex(field => field === event.dataField) !== -1) {
			return;
		}

		const editorOptions = (<any>event).editorOptions;
		editorOptions.disabled = true;
	}

	private async openGroupManagement(user: UserDto): Promise<void> {
		if (!this.canManageGroups) {
			return;
		}

		this.selectedUser = user;
		this.groupManagementVisible = false;
		this.groupManagementLoading = true;
		this.availableGroups = [];
		this.selectedGroupIds = [];
		this.reservedAssignedGroups = [];
		this.reservedAssignedGroupNames = '';
		this.changeDetectorRef.detectChanges();

		try {
			const [availableGroups, usersPerGroup] = await Promise.all([
				firstValueFrom(this.groupService.getAll().pipe(timeout(15000))),
				firstValueFrom(this.groupService.getUsersPerGroup('', 0).pipe(timeout(15000))),
			]);
			const userGroups = usersPerGroup
				.filter(userInGroup => userInGroup.userId === user.id)
				.map(userInGroup => availableGroups.find(group => group.id === userInGroup.groupId))
				.filter((group): group is Group => !!group);
			const assignableGroups = availableGroups.filter(group => !this.isHiddenLegacyGroup(group));
			const visibleGroups = this.canManageProtectedGroups
				? assignableGroups
				: assignableGroups.filter(group => !this.isReservedGroup(group));

			this.availableGroups = [...visibleGroups].sort((left, right) => (left.name ?? '').localeCompare(right.name ?? ''));
			this.reservedAssignedGroups = this.canManageProtectedGroups
				? []
				: [...userGroups]
					.filter(group => !this.isHiddenLegacyGroup(group))
					.filter(group => this.isReservedGroup(group))
					.sort((left, right) => (left.name ?? '').localeCompare(right.name ?? ''));
			this.reservedAssignedGroupNames = this.reservedAssignedGroups
				.map(group => group.name)
				.filter((groupName): groupName is string => !!groupName)
				.join(', ');
			this.selectedGroupIds = userGroups
				.filter(group => this.canManageProtectedGroups || !this.isReservedGroup(group))
				.map(group => group.id)
				.filter((groupId): groupId is number => groupId != null);
			this.groupManagementVisible = true;
		} catch (error) {
			this.closeGroupManagement();
			showErrorSwal(error);
		} finally {
			this.groupManagementLoading = false;
			this.changeDetectorRef.detectChanges();
		}
	}

	private getUserDisplayName(user: UserDto): string {
		const nameParts = [user.firstName, user.lastName].filter(part => !!part);
		const fullName = nameParts.join(' ').trim();
		const userName = user.userName ?? user.username;

		if (fullName && user.email) {
			return `${fullName} (${user.email})`;
		}

		if (fullName) {
			return fullName;
		}

		if (userName && user.email) {
			return `${userName} (${user.email})`;
		}

		return userName ?? user.email ?? `User ${user.id}`;
	}

	private isReservedGroup(group: Group): boolean {
		return !!group.name && AdminUsersManagementGridComponent.restrictedGroupNames.has(group.name);
	}

	private isHiddenLegacyGroup(group: Group): boolean {
		return !!group.name && AdminUsersManagementGridComponent.hiddenLegacyGroupNames.has(group.name);
	}

	private resolveCurrentUserGroupAccess(availableGroups: Group[], usersPerGroup: UserInGroupDto[]): { canManageGroups: boolean; canManageProtectedGroups: boolean } {
		const currentUserId = this.asamcoAuthService.userSession?.user?.userId;
		if (!currentUserId) {
			return { canManageGroups: false, canManageProtectedGroups: false };
		}

		const currentUserGroupNames = usersPerGroup
			.filter(userInGroup => userInGroup.userId === currentUserId)
			.map(userInGroup => availableGroups.find(group => group.id === userInGroup.groupId)?.name ?? userInGroup.groupName)
			.filter((groupName): groupName is string => !!groupName);

		const canManageProtectedGroups = this.hasCurrentUserGroup(currentUserGroupNames, AdminUsersManagementGridComponent.superApplicationLinkAdministratorsGroupName);
		const canManageGroups = canManageProtectedGroups || this.hasCurrentUserGroup(currentUserGroupNames, AdminUsersManagementGridComponent.applicationLinkAdministratorsGroupName);

		return { canManageGroups, canManageProtectedGroups };
	}

	private hasCurrentUserGroup(currentUserGroupNames: string[], groupName: string): boolean {
		return currentUserGroupNames.some(currentUserGroupName => currentUserGroupName.toLocaleLowerCase() === groupName.toLocaleLowerCase());
	}

}
